import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { RiSettings3Line } from "@remixicon/react"

import useRouter from "@/hooks/router/useRoute"
import BasicButton from "@/components/button/BasicButton"
import { useFilterPresetsQuery, useFilterPresetDetailQuery } from "@/hooks/api/useFilterPresetQuery"
import type { FilterPreset } from "@/types/filterPreset"

import { useQuickPdfExport } from "../../hooks/useQuickPdfExport"
import QuickPdfOffscreenRenderer from "../QuickExportPdfDialog"
import QuickReportPresetRow from "./QuickReportPresetRow"

export default function QuickReportWidget() {
  const { t } = useTranslation()
  const router = useRouter()

  const { data: presetSummaries } = useFilterPresetsQuery()

  const { startExport, onDataReady, isActive, isBusy } = useQuickPdfExport()

  const handlePdfExport = useCallback(
    (preset: FilterPreset) => {
      if (!preset.idfPdfTemplate) return
      startExport(preset)
    },
    [startExport],
  )

  return (
    <div className="QuickReportWidget">
      {/* 프리셋이 없을 때 */}
      {!presetSummaries?.length && (
        <div className="QuickReportWidget__empty">
          <p>{t("noPresetYet", "No filter presets yet.")}</p>
          <BasicButton
            type="default-outlined"
            size="sm"
            onClick={() => router.push("/settings/report/filter-preset")}>
            <RiSettings3Line size={16} />
            {t("managePresets", "Manage Presets")}
          </BasicButton>
        </div>
      )}

      {/* 프리셋 목록 — 각 행이 PresetRow */}
      {presetSummaries && presetSummaries.length > 0 && (
        <div className="QuickReportWidget__list">
          {presetSummaries.map((summary) => (
            <QuickReportPresetRowWrapper
              key={summary.idfPreset}
              idfPreset={summary.idfPreset}
              onPdfExport={handlePdfExport}
              isPdfBusy={isBusy}
            />
          ))}
        </div>
      )}

      {/* PDF Export — 오프스크린 렌더러 */}
      <QuickPdfOffscreenRenderer active={isActive} onDataReady={onDataReady} />
    </div>
  )
}

/**
 * 프리셋 상세 로드를 개별 행 단위로 수행하는 래퍼.
 * useFilterPresetDetailQuery는 훅이므로 컴포넌트 레벨에서 호출해야 한다.
 */
function QuickReportPresetRowWrapper({
  idfPreset,
  onPdfExport,
  isPdfBusy,
}: {
  idfPreset: number
  onPdfExport: (preset: FilterPreset) => void
  isPdfBusy: boolean
}) {
  const { data: preset, isLoading } = useFilterPresetDetailQuery(idfPreset)

  if (isLoading || !preset) {
    return (
      <div className="QuickReportPresetRow QuickReportPresetRow--loading">
        <div className="QuickReportPresetRow__skeleton" />
      </div>
    )
  }

  return <QuickReportPresetRow preset={preset} onPdfExport={onPdfExport} isPdfBusy={isPdfBusy} />
}
